import { EASE_OUT_CURVE } from '../../core/params/curve';
import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const FLOW_FIELD_PARAMS = [
  // ── Bewegung ──────────────────────────────────────────────────────────────
  {
    kind: 'int',
    key: 'particles',
    label: 'Partikel',
    min: 100,
    max: 200000,
    step: 1,
    scale: 'log',
    default: 4200,
    bulkRange: [800, 24000],
    group: 'Bewegung',
    tooltip: 'Anzahl der Stromlinien, die gleichzeitig gezeichnet werden.',
  },
  {
    kind: 'int',
    key: 'lifetime',
    label: 'Lebensdauer',
    min: 4,
    max: 1200,
    step: 1,
    scale: 'log',
    default: 190,
    unit: 'Schr.',
    bulkRange: [40, 500],
    group: 'Bewegung',
    tooltip: 'Wie viele Schritte eine Stromlinie läuft, bevor sie neu startet.',
  },
  {
    kind: 'float',
    key: 'stepSize',
    label: 'Schrittweite',
    min: 0.2,
    max: 24,
    step: 0.1,
    default: 2.4,
    unit: 'px',
    bulkRange: [0.8, 8],
    group: 'Bewegung',
    tooltip: 'Distanz pro Schritt. Klein = glatte Kurven, gross = kantige Bahnen.',
  },
  {
    kind: 'int',
    key: 'iterationsPerFrame',
    label: 'Schritte pro Frame',
    min: 1,
    max: 40,
    step: 1,
    default: 6,
    neverRandomise: true,
    group: 'Bewegung',
    tooltip: 'Nur Geschwindigkeit des Aufbaus — beeinflusst das Endbild nicht.',
  },
  {
    kind: 'int',
    key: 'passes',
    label: 'Durchgänge',
    min: 1,
    max: 12,
    step: 1,
    default: 3,
    bulkRange: [1, 6],
    group: 'Bewegung',
    tooltip: 'Wie oft alle Partikel neu gestartet werden. Mehr Durchgänge = dichteres Bild.',
  },
  {
    kind: 'bool',
    key: 'wrap',
    label: 'An Rändern umschlagen',
    default: false,
    group: 'Bewegung',
    tooltip: 'Partikel, die den Canvas verlassen, erscheinen gegenüber wieder.',
  },

  // ── Rauschfeld ────────────────────────────────────────────────────────────
  {
    kind: 'float',
    key: 'noiseScale',
    label: 'Noise-Skala',
    min: 0.0002,
    max: 0.02,
    step: 0.0001,
    scale: 'log',
    default: 0.0022,
    precision: 4,
    // An inverse length: it multiplies a coordinate, so it has to shrink when the
    // canvas grows, or the export would show the pattern at half the size.
    unit: '1/px',
    bulkRange: [0.0006, 0.008],
    group: 'Rauschfeld',
    tooltip: 'Grösse der Wirbel. Klein = weite, ruhige Ströme.',
  },
  {
    kind: 'int',
    key: 'octaves',
    label: 'Oktaven',
    min: 1,
    max: 6,
    step: 1,
    default: 2,
    group: 'Rauschfeld',
    tooltip: 'Zusätzliche Detailebenen im Rauschen.',
  },
  {
    kind: 'float',
    key: 'persistence',
    label: 'Persistenz',
    min: 0.1,
    max: 0.9,
    step: 0.01,
    default: 0.5,
    group: 'Rauschfeld',
    tooltip: 'Wie stark jede weitere Oktave beiträgt.',
    showIf: (v) => (v.octaves as number) > 1,
  },
  {
    kind: 'float',
    key: 'fieldRotation',
    label: 'Feld-Rotation',
    min: -180,
    max: 180,
    step: 1,
    default: 0,
    unit: '°',
    group: 'Rauschfeld',
    tooltip: 'Dreht alle Feldvektoren. 90° verwandelt Quellen in Wirbel.',
  },
  {
    kind: 'float',
    key: 'timeEvolution',
    label: 'Zeit-Entwicklung',
    min: 0,
    max: 0.02,
    step: 0.0001,
    default: 0.0009,
    precision: 4,
    group: 'Rauschfeld',
    tooltip: 'Wandert im Verlauf der Simulation durch die dritte Noise-Dimension.',
  },
  {
    kind: 'bool',
    key: 'curl',
    label: 'Curl-Noise',
    default: true,
    group: 'Rauschfeld',
    tooltip: 'Divergenzfreies Feld: Ströme fliessen ineinander statt zu Quellen zu laufen.',
  },

  // ── Text ──────────────────────────────────────────────────────────────────
  {
    kind: 'enum',
    key: 'textMode',
    label: 'Textwirkung',
    default: 'repel',
    group: 'Text',
    tooltip: 'Wie das Distanzfeld des Textes die Strömung verändert.',
    options: [
      { value: 'repel', label: 'Abstossen' },
      { value: 'attract', label: 'Anziehen' },
      { value: 'tangential', label: 'Umfliessen' },
      { value: 'source', label: 'Quelle' },
      { value: 'sink', label: 'Senke' },
      { value: 'none', label: 'Ignorieren' },
    ],
  },
  {
    kind: 'float',
    key: 'textStrength',
    label: 'Stärke',
    min: 0,
    max: 6,
    step: 0.05,
    default: 2.6,
    bulkRange: [0.6, 4],
    group: 'Text',
    tooltip: 'Wie stark der Text die Feldrichtung überschreibt.',
    showIf: (v) => v.textMode !== 'none',
  },
  {
    kind: 'float',
    key: 'textRadius',
    label: 'Reichweite',
    min: 2,
    max: 600,
    step: 1,
    scale: 'log',
    default: 130,
    unit: 'px',
    bulkRange: [30, 320],
    group: 'Text',
    tooltip: 'Distanz, über die die Textwirkung ausklingt.',
    showIf: (v) => v.textMode !== 'none',
  },
  {
    kind: 'bool',
    key: 'killInside',
    label: 'Im Text abbrechen',
    default: true,
    group: 'Text',
    tooltip: 'Stromlinien enden, sobald sie die Buchstaben berühren — hält den Text frei.',
  },
  {
    kind: 'enum',
    key: 'origin',
    label: 'Startpunkte',
    default: 'outline',
    group: 'Text',
    tooltip: 'Woher die Stromlinien starten.',
    options: [
      { value: 'random', label: 'Zufällig' },
      { value: 'grid', label: 'Raster' },
      { value: 'edges', label: 'Canvas-Rand' },
      { value: 'outline', label: 'Textkontur' },
      { value: 'inside', label: 'Im Text' },
      { value: 'outside', label: 'Nur ausserhalb' },
    ],
  },

  // ── Darstellung ───────────────────────────────────────────────────────────
  {
    kind: 'float',
    key: 'lineWidth',
    label: 'Linienstärke',
    min: 0.1,
    max: 14,
    step: 0.05,
    default: 0.85,
    unit: 'px',
    bulkRange: [0.3, 3],
    group: 'Darstellung',
  },
  {
    kind: 'enum',
    key: 'widthMode',
    label: 'Stärke nach',
    default: 'life',
    segmented: true,
    group: 'Darstellung',
    options: [
      { value: 'fixed', label: 'Fix' },
      { value: 'speed', label: 'Tempo' },
      { value: 'life', label: 'Alter' },
    ],
  },
  {
    kind: 'curve',
    key: 'taper',
    label: 'Verjüngung',
    default: EASE_OUT_CURVE,
    xLabel: 'Lebenslauf',
    yLabel: 'Breite',
    group: 'Darstellung',
    tooltip: 'Verlauf der Strichbreite über die Lebensdauer einer Linie.',
    showIf: (v) => v.widthMode === 'life',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Strichdeckkraft',
    min: 0.01,
    max: 1,
    step: 0.01,
    default: 0.34,
    bulkRange: [0.08, 0.7],
    group: 'Darstellung',
    tooltip: 'Deckkraft eines einzelnen Striches — viele überlagerte ergeben die Dichte.',
  },
  {
    kind: 'float',
    key: 'trailFade',
    label: 'Spur-Verblassen',
    min: 0,
    max: 0.12,
    step: 0.001,
    default: 0,
    precision: 3,
    group: 'Darstellung',
    tooltip: 'Blendet ältere Striche aus. 0 lässt das Bild vollständig aufbauen.',
  },

  // ── Farbe ─────────────────────────────────────────────────────────────────
  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'distance',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'distance', label: 'Nach Textdistanz' },
      { value: 'angle', label: 'Nach Richtung' },
      { value: 'speed', label: 'Nach Tempo' },
      { value: 'life', label: 'Nach Lebensdauer' },
      { value: 'random', label: 'Zufällig' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'acid' },
    group: 'Farbe',
    tooltip: 'Farbverlauf, aus dem die Stromlinien ihre Farbe beziehen.',
  },
] as const satisfies ParamSchema;

export type FlowFieldValues = SchemaValues<typeof FLOW_FIELD_PARAMS>;
